import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../../components/ui/card';
import { DollarSign, Users, TrendingUp, CreditCard, AlertCircle, Building2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../../services/api';
import { getApiErrorMessage } from '../../../utils/apiErrors';

const AdminBillingOverview = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    fetchOverview();
  }, []);

  const fetchOverview = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/admin/billing/overview');
      setOverview(data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load billing overview'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-600">Loading billing overview...</div>
      </div>
    );
  }

  if (!overview) {
    return (
      <div className="p-4 sm:p-6 space-y-6">
        <Card className="p-8 text-center">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <h2 className="text-xl font-semibold mb-2">No Billing Data</h2>
          <p className="text-gray-600">Unable to load billing overview</p>
        </Card>
      </div>
    );
  }

  const conversionPct = overview.total_businesses > 0
    ? ((overview.paying_businesses / overview.total_businesses) * 100).toFixed(1)
    : '0.0';

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Billing Overview</h1>
        <p className="text-gray-600 mt-1">Monitor revenue and subscription metrics</p>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Businesses */}
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Businesses</p>
              <p className="text-3xl font-bold">{overview.total_businesses}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Building2 className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </Card>

        {/* Paying Businesses */}
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Paying Businesses</p>
              <p className="text-3xl font-bold">{overview.paying_businesses}</p>
              <p className="text-xs text-gray-500 mt-1">
                {conversionPct}% conversion
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <Users className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </Card>

        {/* MRR */}
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Monthly Recurring Revenue</p>
              <p className="text-3xl font-bold">₹{(overview.mrr || 0).toLocaleString()}</p>
              <p className="text-xs text-gray-500 mt-1">MRR</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </Card>

        {/* Revenue This Month */}
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Revenue This Month</p>
              <p className="text-3xl font-bold">₹{(overview.total_revenue_this_month || 0).toLocaleString()}</p>
              <p className="text-xs text-gray-500 mt-1">Collected</p>
            </div>
            <div className="p-3 bg-orange-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Plan Distribution & Outstanding Invoices */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Plan Distribution */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <CreditCard className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold">Plan Distribution</h2>
          </div>

          <div className="space-y-4">
            {Object.entries(overview.plan_distribution || {}).length === 0 ? (
              <p className="text-sm text-gray-500">No subscription data yet</p>
            ) : (
              Object.entries(overview.plan_distribution).map(([plan, count]) => {
                const total = overview.total_businesses || 1;
                const percentage = ((count / total) * 100).toFixed(1);

                const colors = {
                  'FREE': 'bg-gray-500',
                  'STARTER': 'bg-blue-500',
                  'GROWTH': 'bg-purple-500'
                };

                return (
                  <div key={plan}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">{plan}</span>
                      <span className="text-sm text-gray-600">{count} ({percentage}%)</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${colors[plan] || 'bg-gray-400'}`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </Card>

        {/* Outstanding Invoices */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertCircle className="w-5 h-5 text-orange-600" />
            <h2 className="text-lg font-semibold">Outstanding Invoices</h2>
          </div>

          <div className="text-center py-8">
            <p className="text-5xl font-bold text-gray-900">{overview.outstanding_invoices}</p>
            <p className="text-sm text-gray-600 mt-2">Unpaid Invoices</p>
            {overview.outstanding_invoices > 0 && (
              <button
                onClick={() => navigate('/admin/billing/invoices?status=DUE')}
                className="mt-4 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
              >
                View Outstanding
              </button>
            )}
          </div>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <button
          onClick={() => navigate('/admin/billing/subscriptions')}
          className="p-4 border border-gray-300 rounded-lg hover:bg-gray-50 text-left"
        >
          <Users className="w-6 h-6 text-blue-600 mb-2" />
          <h3 className="font-semibold mb-1">Manage Subscriptions</h3>
          <p className="text-sm text-gray-600">View and manage all subscriptions</p>
        </button>

        <button
          onClick={() => navigate('/admin/billing/invoices')}
          className="p-4 border border-gray-300 rounded-lg hover:bg-gray-50 text-left"
        >
          <CreditCard className="w-6 h-6 text-green-600 mb-2" />
          <h3 className="font-semibold mb-1">View Invoices</h3>
          <p className="text-sm text-gray-600">Browse all customer invoices</p>
        </button>

        <button
          onClick={() => navigate('/admin/billing/plans')}
          className="p-4 border border-gray-300 rounded-lg hover:bg-gray-50 text-left"
        >
          <TrendingUp className="w-6 h-6 text-purple-600 mb-2" />
          <h3 className="font-semibold mb-1">Manage Plans</h3>
          <p className="text-sm text-gray-600">Edit pricing and plan features</p>
        </button>
      </div>
    </div>
  );
};

export default AdminBillingOverview;
